// Read-only answer bodies rendered as static HTML instead of live ProseMirror
// EditorViews. A quest view used to mount 20+ editors with only ~3 actually
// editable; prosemirror-view's updateStateInner was the top app CPU cost on
// mobile-class hardware. The DOM structure and class names below mirror
// CommonEditor + TiptapEditor exactly so existing CSS, selection handling,
// collapse measurement, and action-token preview scopes keep working.
import React, { useEffect, useMemo } from 'react'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useSearchHeaderHighlight from 'refactor/hooks/useSearchHeaderHighlight'
import tiptapStyles from 'components/shared/tiptap-editor.module.scss'
import fallbackMentionStyles from 'components/shared/mentions.module.scss'
import { isEmpty } from 'lib/value'
import { getReadOnlyShowableValue } from 'components/shared/readOnlyTrailingLinks'
import { generateStaticAnswerHtml } from './staticAnswerHtml'
import {
    activateMentionFromReadOnlyContent,
    activateMemeFromReadOnlyContent,
    MEME_LINK_PREFIX,
} from 'components/shared/tiptap/readOnlyContentActivation'
import { getStaticAnswerAnchorAction } from './staticAnswerLinkClickPolicy'
import {
    BLOCKED_EXTERNAL_NAVIGATION_TOAST,
    getBlockedExternalNavigationUrl,
} from 'components/shared/tiptap/blockedExternalNavigation'
import toast from 'utils/toast'
import 'components/shared/blocked-external-navigation.scss'

// Escape hatch: flip in the console to force every answer back onto the live
// read-only editor path (`localStorage.TREECHAT_DISABLE_STATIC_ANSWERS = 'true'`).
export function staticAnswerBodiesDisabled(): boolean {
    try {
        return localStorage.getItem('TREECHAT_DISABLE_STATIC_ANSWERS') === 'true'
    } catch {
        return false
    }
}

type StaticAnswerBodyProps = {
    value: unknown
    answer?: {
        embeds?: Array<{ id?: unknown } | null | undefined>
    }
    type?: string
    isEmbed?: boolean
    uiId?: string
    editorTestId?: string
    onRendered?: () => void
    plainMemeLinks?: boolean
    // Rendered when the content can't be statically serialized (or the escape
    // hatch is set); must produce the live read-only editor.
    renderFallback?: () => React.ReactNode
}

// Rendered bodies survive the component: a feed mounts and unmounts posts as they
// scroll past, and every remount re-derived the showable value (a deep isEqual)
// and re-serialized the same body. Keyed by the body object itself (answer bodies
// come from the shared answer cache, so a changed body is a new object) plus the
// inputs that change the output.
type RenderedBody = {
    showableValue: ReturnType<typeof getReadOnlyShowableValue>
    html: string | null
}
const renderedBodies = new WeakMap<object, Map<string, RenderedBody>>()

function hasPrivateEmbed(answer: StaticAnswerBodyProps['answer']) {
    return !!answer?.embeds?.some(embed => embed?.id === '__PRIVATE__')
}

export function renderStaticAnswerBody(
    value: unknown,
    options: {
        answer?: StaticAnswerBodyProps['answer']
        isEmbed?: boolean
        plainMemeLinks?: boolean
    },
): RenderedBody {
    const compute = () => {
        const showableValue = getReadOnlyShowableValue(value, {
            answer: options.answer,
            isEmbed: options.isEmbed,
        })
        const html = staticAnswerBodiesDisabled()
            ? null
            : generateStaticAnswerHtml(showableValue, fallbackMentionStyles, {
                  showMemeBrackets: !options.plainMemeLinks,
              })
        return { showableValue, html }
    }
    if (!value || typeof value !== 'object' || staticAnswerBodiesDisabled()) return compute()

    const key = `${options.isEmbed ? 1 : 0}|${hasPrivateEmbed(options.answer) ? 1 : 0}|${
        options.plainMemeLinks ? 1 : 0
    }`
    let byKey = renderedBodies.get(value)
    if (!byKey) {
        byKey = new Map()
        renderedBodies.set(value, byKey)
    }
    let rendered = byKey.get(key)
    if (!rendered) {
        rendered = compute()
        byKey.set(key, rendered)
    }
    return rendered
}

function getTypeClass(type?: string) {
    switch (type) {
        case 'title':
            return fallbackMentionStyles.title || ''
        case 'answer':
            return fallbackMentionStyles.answer || ''
        case 'new-answer':
            return fallbackMentionStyles.newAnswer || ''
        case 'search':
            return fallbackMentionStyles.search || ''
        default:
            return fallbackMentionStyles.answer || ''
    }
}

export default function StaticAnswerBody(props: StaticAnswerBodyProps) {
    const { openRight } = useOpenRight()
    const highlightSearchHeader = useSearchHeaderHighlight()

    // Mirrors CommonEditor's read-only showableValue derivation.
    const { showableValue, html } = useMemo(
        () =>
            renderStaticAnswerBody(props.value, {
                answer: props.answer,
                isEmbed: props.isEmbed,
                plainMemeLinks: props.plainMemeLinks,
            }),
        [props.answer, props.value, props.isEmbed, props.plainMemeLinks],
    )

    useEffect(() => {
        if (html != null) props.onRendered?.()
    }, [html])

    // Parity with CommonEditor: render nothing for empty read-only content.
    if (isEmpty(showableValue)) return null
    if (html == null) return <>{props.renderFallback?.() ?? null}</>

    const handleContentClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement

        // Mention activation (mirrors MentionExtension's click plugin)
        const mentionElement = target.hasAttribute?.('data-mention-id')
            ? target
            : (target.closest?.('[data-mention-id]') as HTMLElement | null)
        if (mentionElement) {
            const mentionId = mentionElement.getAttribute('data-mention-id')
            const mentionType = mentionElement.getAttribute('data-mention-type') || 'user'
            if (mentionType !== 'model' && mentionId) {
                event.preventDefault()
                event.stopPropagation()
                activateMentionFromReadOnlyContent(
                    mentionId,
                    mentionElement,
                    openRight,
                    highlightSearchHeader,
                )
                return
            }
        }

        // Meme activation (mirrors MemeExtension's click plugin); plain links
        // keep their native target=_blank navigation.
        const anchor =
            target.tagName === 'A' ? target : (target.closest?.('a') as HTMLElement | null)
        if (anchor) {
            const href = anchor.getAttribute('href') || ''
            const blockedUrl = getBlockedExternalNavigationUrl(anchor)
            if (blockedUrl) {
                event.preventDefault()
                event.stopPropagation()
                toast.error(BLOCKED_EXTERNAL_NAVIGATION_TOAST, {
                    kind: 'blocked-scam-link',
                    testId: 'blocked-scam-link-toast',
                })
                return
            }

            if (!href) {
                event.preventDefault()
                event.stopPropagation()
                return
            }

            const className = anchor.getAttribute('class') || ''
            const memeClass = fallbackMentionStyles.meme
            const anchorAction = getStaticAnswerAnchorAction(href, className, memeClass)
            if (anchorAction === 'activate-meme') {
                if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    event.stopPropagation()
                    return
                }
                event.preventDefault()
                event.stopPropagation()
                // Meme links carry a real /meme/:text href now, so the meme
                // itself comes from the anchor's own text; the sentinel prefix
                // is still honoured for content stored before that change.
                const memeContent = href.startsWith(MEME_LINK_PREFIX)
                    ? href.slice(MEME_LINK_PREFIX.length)
                    : (anchor.textContent || '').trim()
                if (!memeContent) return
                activateMemeFromReadOnlyContent(memeContent, anchor, openRight)
                return
            }

            if (anchorAction === 'navigate') {
                event.stopPropagation()
            }
        }
    }

    return (
        <div
            className={cn('common-editor-comp fs-mask', isMobile && 'is-mobile', props.type)}
            data-paste-image={props.uiId}
            data-mention-editor="true"
            data-testid={props.editorTestId}
            onClick={handleContentClick}
        >
            <div
                className={`${
                    tiptapStyles.tiptapEditorContainer
                } tiptap-editor-container ${getTypeClass(props.type)} readonly`}
                data-mention-editor="true"
            >
                <div className={`${tiptapStyles.editorContent} tiptap-editor-content`}>
                    {/* contentEditable={false} keeps [contenteditable='false'] CSS
                        selectors (read-only action tokens) applying as they did to
                        the live read-only editor DOM. */}
                    <div
                        className="tiptap ProseMirror tiptap-prose knov-static-answer-body"
                        style={{ outline: 'none' }}
                        contentEditable={false}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                </div>
            </div>
        </div>
    )
}
